import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { DownloadIcon } from '@/assets/icons/index'
import AssignedCompliancesStatus from '@/components/dashboard/AssignedCompliancesStatus'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  getDashboardCompliances,
  downloadDashboardCompliances,
} from '@/api/dashboardApi'
import { saveBlobAsFile } from '@/api/apiUtils'

const ComplianceTab = () => {
  // ==================== Hooks ====================
  const optionsLoaded = useSelector(
    state => state.dashboardFilter.optionsLoaded
  )
  // The committed filters the tab fetches on. Changes only on Apply / Reset
  // (and the initial options load) — the sole trigger for a reload.
  const appliedFilters = useSelector(
    state => state.dashboardFilter.appliedFilters
  )

  // ==================== State ====================
  // Raw countries array straight from the compliances API response.
  const [countries, setCountries] = useState([])
  // Turns true once the compliance tree has been fetched.
  const [dataLoaded, setDataLoaded] = useState(false)
  // Guards the download button against concurrent clicks.
  const [downloading, setDownloading] = useState(false)

  // ==================== Component Helpers ====================
  // Fetches the compliance tree for the committed filters. Memoized so it can
  // be reused by the effect and handed to descendants as a refresh callback
  // without changing identity on every render. When `silent` is true the
  // full-screen loader is skipped so the accordion tree stays mounted and the
  // user keeps the accordion/page they were on (used for post-save refreshes).
  const fetchCompliances = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setDataLoaded(false)
      }
      try {
        const response = await getDashboardCompliances(appliedFilters)
        const countriesData = response?.data?.data
        if (response?.success && countriesData) {
          setCountries(countriesData)
        } else {
          setCountries([])
        }
      } finally {
        if (!silent) {
          setDataLoaded(true)
        }
      }
    },
    [appliedFilters]
  )

  // Silent refresh handed to descendants: refetches the tree in the background
  // after a row edit so progress counts/totals update without unmounting the
  // open accordions.
  const refreshCompliances = useCallback(
    () => fetchCompliances({ silent: true }),
    [fetchCompliances]
  )

  // ==================== Effects ====================
  // Fetch on initial options load and whenever the committed filters change
  // (Apply / Reset). Shows the loader each time.
  useEffect(() => {
    if (!optionsLoaded) {
      return
    }
    fetchCompliances()
  }, [optionsLoaded, fetchCompliances])

  // ==================== Derived Data ====================
  // Stable period-range object so child accordions don't see a brand-new prop
  // reference on every ComplianceTab render.
  const periodRange = useMemo(
    () => ({
      fromYear: appliedFilters.fromYear,
      fromMonth: appliedFilters.fromMonth,
      toYear: appliedFilters.toYear,
      toMonth: appliedFilters.toMonth,
    }),
    [
      appliedFilters.fromYear,
      appliedFilters.fromMonth,
      appliedFilters.toYear,
      appliedFilters.toMonth,
    ]
  )

  // ==================== Handlers ====================
  // Downloads the compliances Excel for the committed filters and saves it.
  const handleDownload = async () => {
    if (downloading) {
      return
    }
    setDownloading(true)
    try {
      const result = await downloadDashboardCompliances(appliedFilters)
      if (result?.success) {
        saveBlobAsFile(result.data, 'Compliances.xlsx')
      }
    } finally {
      setDownloading(false)
    }
  }

  // ==================== Return ====================
  if (!dataLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
          my: 2,
        }}
      >
        <Typography
          sx={{
            color: 'var(--forbrand-primary)',
            fontSize: 'var(--font-size-lg)',
          }}
        >
          Loading...
        </Typography>
      </Box>
    )
  }

  if (!countries.length) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
          my: 2,
        }}
      >
        <Typography
          sx={{
            color: 'var(--forbrand-primary)',
            fontSize: 'var(--font-size-lg)',
          }}
        >
          No compliance data available for the selected filters
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, my: 2 }}>
      {/* Outer Wrapper */}
      <Box
        sx={{
          border: '1px solid var(--border-subtel)',
          backgroundColor: 'var(--white-color)',
          p: 2,
        }}
      >
        {/* Wrapper Header - Download icon on the right */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            pb: 2,
          }}
        >
          <Tooltip title="Download">
            <span>
              <IconButton
                size="small"
                onClick={handleDownload}
                disabled={downloading}
                sx={{ color: 'var(--forbrand-secondary)' }}
              >
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Country Accordions - handled internally by AssignedCompliancesStatus */}
        <AssignedCompliancesStatus
          countries={countries}
          periodRange={periodRange}
          onComplianceUpdated={refreshCompliances}
        />
      </Box>
    </Box>
  )
}

export default ComplianceTab
